import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Plus, Pencil, Trash2, Eye, EyeOff, Loader2, X,
  Image as ImageIcon, Layout, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { GlassCard } from '@/components/vendor/GlassCard';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * MasterTemplates — manage master-authored Website Builder templates.
 *
 * Top of page lists ALL custom templates (active + inactive) with hover-edit /
 * delete / toggle-active. "Create new template" CTA in the top-right opens a
 * blank template authoring flow. Built-in system templates are shown below
 * as read-only references (linked to the vendor preview at /master/template/:id).
 */
export default function MasterTemplates() {
  const navigate = useNavigate();
  const token = localStorage.getItem('masterToken') || localStorage.getItem('master_token') || localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(true);
  const [custom, setCustom] = useState([]);
  const [editorState, setEditorState] = useState(null); // null | { mode: 'new'|'edit', template? }
  const [pendingDelete, setPendingDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/master/custom-templates`, { headers });
      setCustom(r.data?.templates || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      if (editorState?.mode === 'new') {
        await axios.post(`${API}/master/custom-templates`, payload, { headers });
        toast.success('Template created — live for all clients');
      } else if (editorState?.mode === 'edit' && editorState.template?.id) {
        await axios.put(`${API}/master/custom-templates/${editorState.template.id}`, payload, { headers });
        toast.success('Template updated — changes live immediately');
      }
      setEditorState(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (t) => {
    try {
      await axios.put(`${API}/master/custom-templates/${t.id}`, { is_active: !t.is_active }, { headers });
      toast.success(t.is_active ? 'Template hidden from clients' : 'Template visible to clients');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    try {
      await axios.delete(`${API}/master/custom-templates/${pendingDelete.id}`, { headers });
      toast.success('Template deleted');
      setPendingDelete(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-8" data-testid="master-templates-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-syne" data-testid="master-templates-title">Templates</h1>
          <p className="text-sm text-[#9CA3AF] mt-1">
            Manage Website Builder templates available to your clients. Custom templates go live immediately on save.
          </p>
        </div>
        <button
          onClick={() => setEditorState({ mode: 'new' })}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold uppercase tracking-wider text-white bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] shadow-[0_0_20px_rgba(139,92,246,0.35)] hover:shadow-[0_0_28px_rgba(139,92,246,0.55)] transition-all"
          data-testid="master-create-template-btn"
        >
          <Plus size={16} /> Create new template
        </button>
      </div>

      {/* Custom templates grid */}
      <GlassCard className="p-6">
        <div className="flex items-center justify-between mb-4">
          <p className="text-[10px] tracking-[0.18em] uppercase text-[#A78BFA] font-bold">Your custom templates</p>
          <p className="text-xs text-white/50">{custom.length} total</p>
        </div>

        {loading ? (
          <div className="py-12 flex items-center justify-center text-white/60">
            <Loader2 size={20} className="animate-spin" />
          </div>
        ) : custom.length === 0 ? (
          <div className="py-16 text-center text-white/60 border border-dashed border-white/10 rounded-xl">
            <Layout size={28} className="mx-auto mb-3 opacity-50" />
            <p className="text-sm font-medium">No custom templates yet</p>
            <p className="text-xs mt-1 text-white/40">Click "Create new template" to build one. Clients will see it immediately.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="master-templates-grid">
            {custom.map(t => (
              <CustomTemplateCard
                key={t.id}
                template={t}
                onEdit={() => setEditorState({ mode: 'edit', template: t })}
                onDelete={() => setPendingDelete(t)}
                onToggle={() => handleToggle(t)}
              />
            ))}
          </div>
        )}
      </GlassCard>

      {/* Editor modal */}
      {editorState && (
        <TemplateEditorModal
          mode={editorState.mode}
          template={editorState.template}
          onSave={handleSave}
          onClose={() => setEditorState(null)}
          saving={saving}
        />
      )}

      {/* Delete confirm */}
      {pendingDelete && (
        <div className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4" data-testid="master-template-delete-modal">
          <GlassCard className="p-6 max-w-md w-full">
            <div className="flex items-start gap-3">
              <AlertCircle size={22} className="text-[#FB7185] shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-bold text-white font-syne">Delete &ldquo;{pendingDelete.name}&rdquo;?</h3>
                <p className="text-sm text-white/60 mt-1">
                  This removes the template from the gallery for all clients. Any sites already built with it are unaffected.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setPendingDelete(null)} className="px-4 py-2 rounded-lg text-sm text-white/70 hover:text-white hover:bg-white/[0.06]" data-testid="master-template-delete-cancel">Cancel</button>
              <button onClick={handleDelete} className="px-4 py-2 rounded-lg text-sm font-bold uppercase tracking-wider text-white bg-[#FB7185] hover:bg-[#F43F5E]" data-testid="master-template-delete-confirm">Delete</button>
            </div>
          </GlassCard>
        </div>
      )}
    </div>
  );
}

function CustomTemplateCard({ template, onEdit, onDelete, onToggle }) {
  const accent = template.accent || '#8B5CF6';
  return (
    <div
      className="group relative bg-[#15152E]/70 border border-white/[0.06] rounded-2xl overflow-hidden hover:border-[#A78BFA]/30 transition-all"
      data-testid={`master-template-card-${template.id}`}
    >
      {/* Thumbnail */}
      <div className="relative aspect-[16/10] overflow-hidden bg-gradient-to-br from-[#1E1B4B] to-[#0E0E23]">
        {template.thumbnail_url ? (
          <img src={template.thumbnail_url} alt={template.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/30">
            <ImageIcon size={40} />
          </div>
        )}
        {/* Inactive overlay */}
        {!template.is_active && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <span className="px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] font-bold text-white bg-white/10 border border-white/20">Hidden</span>
          </div>
        )}
        {/* Hover actions */}
        <div className="absolute inset-0 bg-black/65 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2.5">
          <button
            onClick={onEdit}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-bold uppercase tracking-wider text-white bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] hover:shadow-[0_0_22px_rgba(139,92,246,0.6)] transition-all"
            data-testid={`master-template-edit-${template.id}`}
          >
            <Pencil size={13} /> Edit Template
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={onToggle}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] uppercase tracking-wider text-white/90 bg-white/[0.08] border border-white/15 hover:bg-white/[0.15] transition-all"
              data-testid={`master-template-toggle-${template.id}`}
              title={template.is_active ? 'Hide from clients' : 'Show to clients'}
            >
              {template.is_active ? <><EyeOff size={11} /> Hide</> : <><Eye size={11} /> Activate</>}
            </button>
            <button
              onClick={onDelete}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] uppercase tracking-wider text-[#FB7185] bg-[#FB7185]/10 border border-[#FB7185]/30 hover:bg-[#FB7185]/20"
              data-testid={`master-template-delete-${template.id}`}
            >
              <Trash2 size={11} /> Delete
            </button>
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-1.5">
          <span className="px-2 py-0.5 rounded-full text-[9px] font-medium text-white/80 uppercase tracking-wider" style={{ background: `${accent}30`, border: `1px solid ${accent}40` }}>
            {template.category || 'Custom'}
          </span>
          <span className="text-[9px] uppercase tracking-wider text-white/40">{template.tier}</span>
        </div>
        <h3 className="text-white font-bold font-syne text-base truncate" data-testid={`master-template-name-${template.id}`}>{template.name}</h3>
        <p className="text-[12px] text-white/55 mt-1 line-clamp-2 leading-relaxed">{template.desc || 'No description.'}</p>
        <div className="mt-3 pt-3 border-t border-white/[0.06] flex items-center justify-between text-[10px] text-white/40">
          <span>{(template.blocks || []).length} blocks</span>
          <span>Updated {new Date(template.updated_at).toLocaleDateString()}</span>
        </div>
      </div>
    </div>
  );
}

function TemplateEditorModal({ mode, template, onSave, onClose, saving }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: template?.name || '',
    category: template?.category || 'Custom',
    desc: template?.desc || '',
    accent: template?.accent || '#8B5CF6',
    thumbnail_url: template?.thumbnail_url || '',
    tier: template?.tier || 'standard',
    is_active: template?.is_active !== false,
    blocks: template?.blocks || [],
  });

  const submit = (e) => {
    e?.preventDefault();
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    onSave(form);
  };

  // Authoring tip: the actual block authoring happens in the website-builder
  // canvas itself — we open it in a special "template-authoring" mode in a
  // future iteration. For now we capture metadata + accept a pasted blocks
  // JSON or copy from an existing site.
  const blockCount = (form.blocks || []).length;

  return (
    <div className="fixed inset-0 z-[180] bg-[#0A0A1F] overflow-y-auto" data-testid="master-template-editor-modal">
      <div className="sticky top-0 z-10 bg-[#0A0A1F]/95 backdrop-blur-xl border-b border-white/[0.06] px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/[0.06] text-[#9CA3AF] hover:text-white" aria-label="Close" data-testid="master-template-editor-close">
            <X size={18} />
          </button>
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#64748B] font-bold">{mode === 'new' ? 'New template' : 'Edit template'}</p>
            <h2 className="text-lg font-bold text-white truncate font-syne">{form.name || 'Untitled template'}</h2>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium text-[#9CA3AF] hover:bg-white/[0.06] hover:text-white">Discard</button>
          <button onClick={submit} disabled={saving} className="px-5 py-2 rounded-lg text-sm font-bold text-white bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] shadow-[0_0_15px_rgba(139,92,246,0.3)] hover:shadow-[0_0_25px_rgba(139,92,246,0.5)] disabled:opacity-50" data-testid="master-template-editor-save">
            {saving ? 'Saving…' : (mode === 'new' ? 'Create template' : 'Save changes')}
          </button>
        </div>
      </div>

      <form onSubmit={submit} className="max-w-[1100px] mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT */}
        <div className="lg:col-span-2 space-y-5">
          <GlassCard className="p-5">
            <label className="text-[10px] uppercase tracking-[0.18em] text-[#9CA3AF] font-bold">Name *</label>
            <input
              value={form.name}
              onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
              placeholder="e.g., Bold Bakery"
              className="mt-1.5 w-full bg-white/[0.04] border border-white/10 text-white rounded-xl px-3 py-2"
              data-testid="master-template-name-input"
            />
            <label className="block mt-4 text-[10px] uppercase tracking-[0.18em] text-[#9CA3AF] font-bold">Description</label>
            <textarea
              value={form.desc}
              onChange={(e) => setForm(p => ({ ...p, desc: e.target.value }))}
              rows={4}
              placeholder="Describe what this template is best for…"
              className="mt-1.5 w-full bg-white/[0.04] border border-white/10 text-white rounded-xl px-3 py-2"
              data-testid="master-template-desc-input"
            />
          </GlassCard>

          <GlassCard className="p-5">
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#9CA3AF] font-bold mb-3">Blocks</p>
            <p className="text-[12px] text-white/60">
              {blockCount === 0 ? 'No blocks yet — this template will start empty.' : `${blockCount} blocks defined.`}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  // Open the live website-builder canvas in author mode.
                  // Author mode reads ?template_target=<id> and on save commits
                  // the canvas blocks back to the template doc via PUT.
                  if (mode === 'edit' && template?.id) {
                    navigate(`/website-builder/content?template_target=${template.id}`);
                  } else {
                    toast.message('Save the template first, then click Edit Blocks to lay it out in the live canvas.');
                  }
                }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider text-white bg-white/[0.06] border border-white/10 hover:bg-white/[0.10]"
                data-testid="master-template-edit-blocks"
                disabled={mode === 'new'}
                title={mode === 'new' ? 'Save the template first to enable block editing' : 'Edit blocks in the live builder canvas'}
              >
                <Pencil size={12} /> Edit Blocks in Builder
              </button>
              <button
                type="button"
                onClick={() => {
                  const raw = prompt('Paste a JSON array of blocks (advanced). Leave empty to cancel.');
                  if (!raw) return;
                  try {
                    const arr = JSON.parse(raw);
                    if (!Array.isArray(arr)) throw new Error('Not an array');
                    setForm(p => ({ ...p, blocks: arr }));
                    toast.success(`${arr.length} blocks loaded`);
                  } catch (err) {
                    toast.error('Invalid JSON');
                  }
                }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider text-white/70 bg-white/[0.04] border border-white/10 hover:bg-white/[0.08]"
                data-testid="master-template-paste-blocks"
              >
                Paste blocks JSON
              </button>
            </div>
            <p className="text-[10px] text-white/40 mt-3">
              Tip: Author the template in the website-builder canvas after saving, then click <strong className="text-white/70">Edit Blocks in Builder</strong> to commit the live layout back to this template.
            </p>
          </GlassCard>
        </div>

        {/* RIGHT */}
        <aside className="space-y-5">
          <GlassCard className="p-5">
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#9CA3AF] font-bold mb-3">Visibility</p>
            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-sm text-white">Active for clients</span>
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm(p => ({ ...p, is_active: e.target.checked }))}
                className="accent-[#8B5CF6]"
                data-testid="master-template-active-toggle"
              />
            </label>
          </GlassCard>

          <GlassCard className="p-5">
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#9CA3AF] font-bold mb-3">Categorization</p>
            <label className="text-[#94A3B8] text-xs">Category</label>
            <input
              value={form.category}
              onChange={(e) => setForm(p => ({ ...p, category: e.target.value }))}
              placeholder="e.g., E-commerce / Service / Portfolio"
              className="mt-1.5 w-full bg-white/[0.04] border border-white/10 text-white rounded-xl px-3 py-2 text-sm"
              data-testid="master-template-category-input"
            />
            <label className="text-[#94A3B8] text-xs mt-3 block">Tier</label>
            <select
              value={form.tier}
              onChange={(e) => setForm(p => ({ ...p, tier: e.target.value }))}
              className="mt-1.5 w-full bg-white/[0.04] border border-white/10 text-white rounded-xl px-3 py-2 text-sm"
              data-testid="master-template-tier-input"
            >
              <option value="free">Free</option>
              <option value="standard">Standard</option>
              <option value="premium">Premium</option>
            </select>
            <label className="text-[#94A3B8] text-xs mt-3 block">Accent color</label>
            <div className="mt-1.5 flex items-center gap-2">
              <input
                type="color"
                value={form.accent}
                onChange={(e) => setForm(p => ({ ...p, accent: e.target.value }))}
                className="w-10 h-10 rounded-lg bg-transparent border border-white/10 cursor-pointer"
                data-testid="master-template-accent-input"
              />
              <input
                type="text"
                value={form.accent}
                onChange={(e) => setForm(p => ({ ...p, accent: e.target.value }))}
                className="flex-1 bg-white/[0.04] border border-white/10 text-white rounded-xl px-3 py-2 text-sm font-mono"
              />
            </div>
          </GlassCard>

          <GlassCard className="p-5">
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#9CA3AF] font-bold mb-3">Cover image</p>
            <input
              value={form.thumbnail_url}
              onChange={(e) => setForm(p => ({ ...p, thumbnail_url: e.target.value }))}
              placeholder="https://… (paste an image URL)"
              className="w-full bg-white/[0.04] border border-white/10 text-white rounded-xl px-3 py-2 text-sm"
              data-testid="master-template-thumbnail-input"
            />
            {form.thumbnail_url && (
              <div className="mt-3 rounded-lg overflow-hidden aspect-[16/10] bg-black/40">
                <img src={form.thumbnail_url} alt="Cover preview" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.opacity = 0.2; }} />
              </div>
            )}
          </GlassCard>
        </aside>
      </form>
    </div>
  );
}
